import { Component, input, computed, ChangeDetectionStrategy, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SeAboutOrgComponent } from "./se.about.org.component";
import { SeContactTileComponent } from "./se.contact.tile.component";
import { SeEventTileComponent } from "./se.event.tile.component";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { SlicePipe } from "@angular/common";
import { SeEvent } from "../event/se.event.details.view.component";
import { S25States } from "../../../services/state/state.map";

@TypeManagerDecorator("s25-ng-se-org-details-view")
@Component({
    selector: "s25-ng-se-org-details-view",
    template: `
        <div class="org-header">
            @if (isExternalView) {
                <h1 class="org-name">{{ orgDetails().name }}</h1>
            }
            <se-about-org [orgId]="orgId()" [mission]="orgDetails().mission" [imageAttr]="orgDetails().imageAttr">
            </se-about-org>
        </div>

        @if (primaryContact()) {
            <div class="primary-contact-section">
                <se-contact-tile
                    [name]="primaryContact().itemName"
                    [email]="primaryContact().email"
                    [imageUrl]="primaryContact().itemImage"
                    [header]="'Primary Contact'"
                >
                </se-contact-tile>
            </div>
        }

        @if (upcomingEvents().length) {
            <div class="org-section">
                <h2>Upcoming Events</h2>
                <div class="events-grid">
                    @for (event of upcomingEvents() | slice: 0 : viewableEvents(); track event.itemId) {
                        <se-event-tile [event]="event" [routingStateName]="routingName"> </se-event-tile>
                    }
                </div>
                @if (upcomingEvents().length > viewableEvents()) {
                    <button class="c-textButton" (click)="viewMoreEvents()">See More Events</button>
                }
            </div>
        } @else {
            <div class="no-results">No Events Found</div>
        }
    `,
    styles: `
        .org-header {
            text-align: center;
            margin-bottom: 2rem;
            padding-bottom: 2rem;
            border-bottom: 2px solid #e5e5e5;
        }

        .org-name {
            font-size: 2.5rem;
            color: #333;
            margin: 0 0 0.5rem 0;
            font-weight: 700;
        }

        .org-section {
            margin-block: 2rem;
            border-radius: 12px;
        }

        .org-section h2 {
            color: #333;
            font-size: 1.8rem;
            margin: 0 0 1.5rem 0;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #f0f0f0;
        }

        .primary-contact-section {
            margin-top: 2rem;
        }

        .primary-contact-section h3 {
            color: #555;
            font-size: 1.3rem;
            margin: 0 0 1rem 0;
            font-weight: 500;
        }

        .events-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
        }

        .c-textButton {
            display: block;
            margin: 1rem auto 0;
        }

        .no-results {
            color: #6c6c6c;
            font-size: 1.3rem;
            text-align: center;
            padding: 1em 0;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SeAboutOrgComponent, SeContactTileComponent, SeEventTileComponent, SlicePipe],
})
export class SeOrgDetailsViewComponent {
    orgDetails = input.required<StudentEngagementOrg.details>();
    orgId = input.required<number>();

    primaryContact = computed<S25ItemI>(() => this.orgDetails().primaryContact ?? null);
    upcomingEvents = computed<SeEvent[]>(() => this.orgDetails().upcomingEvents ?? []);

    isExternalView: boolean;
    viewableEvents = signal(10);
    routingName = S25States.names.studentEngagementEvent;

    viewMoreEvents() {
        this.viewableEvents.update((prev) => prev + 10);
    }
}
